/* iter-glow1 · Sep-2026 — Breathing glow for writing-lifecycle nudges.
 *
 * Purely visual companion to the 3 event-driven tours in `lib/tours.js`
 * (`author-begin-story`, `author-ai-next-step`, `author-request-editor-review`).
 * When a tour's underlying condition is true, the caller also fires
 * `startGlow(id, anchorTestId)` alongside `runTour(...)`. If the writer
 * dismisses/ignores the tooltip, this soft gold ↔ transparent pulse keeps
 * quietly outlining the next logical control until they either act on it
 * (clicking the anchor auto-clears the glow) or the underlying condition
 * goes away (caller's effect cleanup calls `stopGlow(id)`).
 *
 * Strictly opt-in to "Guided" mode — Light and Off never render a glow,
 * matching the existing Guided/Light/Off picker in Settings.
 */
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';

const GLOW_PAD = 4;

function promptMode() {
  try { return window.localStorage.getItem('pw_prompt_mode') || 'guided'; }
  catch { return 'guided'; }
}

/** Start (or replace) the breathing glow for a given nudge id, spotlighting
 *  the element with `data-testid="{anchor}"`. No-op outside Guided mode. */
export function startGlow(id, anchor) {
  if (!id || !anchor || promptMode() !== 'guided') return;
  window.dispatchEvent(new CustomEvent('pw:glow-start', { detail: { id, anchor } }));
}

/** Stop the glow for `id` (no-op if a different nudge is currently active). */
export function stopGlow(id) {
  if (!id) return;
  window.dispatchEvent(new CustomEvent('pw:glow-stop', { detail: { id } }));
}

export default function GlowNudge() {
  const [glow, setGlow] = useState(null); // { id, anchor }
  const [rect, setRect] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false); // any Radix/shadcn dialog present

  // ── Dialog-open detector (MutationObserver) ────────────────────────────────
  // If ANY modal dialog is open in the DOM, drop the glow overlay behind it.
  useEffect(() => {
    const check = () => {
      setDialogOpen(document.querySelectorAll('[role="dialog"]').length > 0);
    };
    check(); // initial state
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onStart = (e) => {
      const { id, anchor } = e.detail || {};
      if (!id || !anchor) return;
      setGlow({ id, anchor });
    };
    const onStop = (e) => {
      const { id } = e.detail || {};
      setGlow((cur) => (cur && cur.id === id ? null : cur));
    };
    window.addEventListener('pw:glow-start', onStart);
    window.addEventListener('pw:glow-stop', onStop);
    return () => {
      window.removeEventListener('pw:glow-start', onStart);
      window.removeEventListener('pw:glow-stop', onStop);
    };
  }, []);

  const measure = useCallback(() => {
    if (!glow) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${glow.anchor}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({
      x: r.left - GLOW_PAD, y: r.top - GLOW_PAD,
      w: r.width + GLOW_PAD * 2, h: r.height + GLOW_PAD * 2,
    });
  }, [glow]);

  useLayoutEffect(() => {
    if (!glow) { setRect(null); return undefined; }
    measure();
    const iv = setInterval(measure, 500);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [glow, measure]);

  // Clicking the nudged control IS the next step — clear immediately
  // instead of waiting on the caller's own effect cleanup.
  useEffect(() => {
    if (!glow) return undefined;
    const onDown = (e) => {
      const el = document.querySelector(`[data-testid="${glow.anchor}"]`);
      if (el && (el === e.target || el.contains(e.target))) setGlow(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [glow]);

  if (!glow || !rect || promptMode() !== 'guided' || dialogOpen) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[70]"
      data-testid="glow-nudge-overlay"
      data-glow-id={glow.id}
    >
      <div
        className="pw-breathing-glow absolute rounded-md"
        style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      />
    </div>
  );
}
